import { useState, useMemo } from 'react';
import type { Finanziamento } from '../../types';
import { formatCurrency } from '../../utils/pricing';
import {
  calculateMortgagePayment,
  calculateTotalInterest,
  calculateTotalPaid,
} from '../../services/financeCalculations';

type TipoTasso = 'fisso' | 'variabile' | 'misto';

interface MutuoFormProps {
  finanziamento?: Finanziamento;
  onSave: (data: Partial<Finanziamento>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function MutuoForm({
  finanziamento,
  onSave,
  onCancel,
  isLoading,
}: MutuoFormProps) {
  const [formData, setFormData] = useState({
    nome: finanziamento?.nome || 'Mutuo ristrutturazione',
    importo: finanziamento?.importo || '',
    tassoInteresse: finanziamento
      ? String(parseFloat(finanziamento.tassoInteresse) * 100)
      : '3.5',
    durataAnni: finanziamento?.durataAnni || 20,
    note: finanziamento?.note || '',
  });

  const [tipoTasso, setTipoTasso] = useState<TipoTasso>('fisso');

  // Variabile fields
  const [euribor, setEuribor] = useState('3.2');
  const [spread, setSpread] = useState('1.3');

  // Misto fields
  const [tanFisso, setTanFisso] = useState('3.5');
  const [durataFisso, setDurataFisso] = useState(5);
  const [euriborMisto, setEuriborMisto] = useState('3.2');
  const [spreadMisto, setSpreadMisto] = useState('1.3');

  // Effective TAN percentage based on tipoTasso
  const tassoEffettivo = useMemo(() => {
    switch (tipoTasso) {
      case 'fisso':
        return parseFloat(formData.tassoInteresse) || 0;
      case 'variabile':
        return (parseFloat(euribor) || 0) + (parseFloat(spread) || 0);
      case 'misto':
        return parseFloat(tanFisso) || 0; // Use fisso rate for main calculation
      default:
        return 0;
    }
  }, [tipoTasso, formData.tassoInteresse, euribor, spread, tanFisso]);

  const calculations = useMemo(() => {
    const importo = parseFloat(formData.importo) || 0;
    const tasso = tassoEffettivo / 100;
    const durata = formData.durataAnni;

    const rataMensile = calculateMortgagePayment(importo, tasso, durata);
    const totalePagato = calculateTotalPaid(rataMensile, durata);
    const totaleInteressi = calculateTotalInterest(importo, rataMensile, durata);

    return {
      rataMensile,
      totalePagato,
      totaleInteressi,
    };
  }, [formData.importo, tassoEffettivo, formData.durataAnni]);

  // Scenarios for variabile
  const scenari = useMemo(() => {
    if (tipoTasso !== 'variabile') return null;

    const importo = parseFloat(formData.importo) || 0;
    const durata = formData.durataAnni;
    const euriborVal = parseFloat(euribor) || 0;
    const spreadVal = parseFloat(spread) || 0;

    const ottimistico = (Math.max(0, euriborVal - 1) + spreadVal) / 100;
    const base = (euriborVal + spreadVal) / 100;
    const pessimistico = (euriborVal + 2 + spreadVal) / 100;

    return {
      ottimistico: {
        tasso: Math.max(0, euriborVal - 1) + spreadVal,
        rata: calculateMortgagePayment(importo, ottimistico, durata),
      },
      base: {
        tasso: euriborVal + spreadVal,
        rata: calculateMortgagePayment(importo, base, durata),
      },
      pessimistico: {
        tasso: euriborVal + 2 + spreadVal,
        rata: calculateMortgagePayment(importo, pessimistico, durata),
      },
    };
  }, [tipoTasso, formData.importo, formData.durataAnni, euribor, spread]);

  // Misto scenario: rata periodo fisso + rata stimata periodo variabile
  const scenarioMisto = useMemo(() => {
    if (tipoTasso !== 'misto') return null;

    const importo = parseFloat(formData.importo) || 0;
    const durataTotale = formData.durataAnni;
    const tanFissoVal = (parseFloat(tanFisso) || 0) / 100;
    const tanVariabileVal =
      ((parseFloat(euriborMisto) || 0) + (parseFloat(spreadMisto) || 0)) / 100;
    const durataFissoVal = Math.min(durataFisso, durataTotale);
    const durataVariabile = durataTotale - durataFissoVal;

    const rataFisso = calculateMortgagePayment(importo, tanFissoVal, durataTotale);

    // Approximate remaining principal after the fixed period
    const mesiFixedPeriod = durataFissoVal * 12;
    let balance = importo;
    const monthlyRateFixed = tanFissoVal / 12;
    for (let i = 0; i < mesiFixedPeriod; i++) {
      const interest = balance * monthlyRateFixed;
      const principalPaid = rataFisso - interest;
      balance = Math.max(0, balance - principalPaid);
    }

    const rataVariabile =
      durataVariabile > 0
        ? calculateMortgagePayment(balance, tanVariabileVal, durataVariabile)
        : 0;

    return {
      rataFisso,
      durataFissoAnni: durataFissoVal,
      rataVariabile,
      durataVariabileAnni: durataVariabile,
      tanVariabile: (parseFloat(euriborMisto) || 0) + (parseFloat(spreadMisto) || 0),
    };
  }, [
    tipoTasso,
    formData.importo,
    formData.durataAnni,
    tanFisso,
    durataFisso,
    euriborMisto,
    spreadMisto,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const tassoDecimale = (tassoEffettivo / 100).toFixed(4);

    // Build note with rate type details
    let noteDettaglio = formData.note || '';
    if (tipoTasso === 'variabile') {
      const prefix = `Variabile: Euribor 3M ${euribor}% + Spread ${spread}%`;
      noteDettaglio = noteDettaglio ? `${prefix} — ${noteDettaglio}` : prefix;
    } else if (tipoTasso === 'misto') {
      const prefix = `Misto: Fisso ${tanFisso}% per ${durataFisso} anni, poi Variabile Euribor ${euriborMisto}% + Spread ${spreadMisto}%`;
      noteDettaglio = noteDettaglio ? `${prefix} — ${noteDettaglio}` : prefix;
    }

    onSave({
      tipo: 'mutuo',
      nome: formData.nome,
      importo: formData.importo,
      tassoInteresse: tassoDecimale,
      durataAnni: formData.durataAnni,
      rataMensile: calculations.rataMensile.toFixed(2),
      note: noteDettaglio || null,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'durataAnni' ? parseInt(value, 10) : value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="input-group">
        <label htmlFor="nome" className="input-label">
          Nome Mutuo
        </label>
        <input
          type="text"
          id="nome"
          name="nome"
          value={formData.nome}
          onChange={handleChange}
          className="w-full"
          placeholder="es. Mutuo ristrutturazione BancaXYZ"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="input-group">
          <label htmlFor="importo" className="input-label">
            Importo Richiesto (€)
          </label>
          <input
            type="number"
            id="importo"
            name="importo"
            value={formData.importo}
            onChange={handleChange}
            className="w-full"
            min="0"
            step="1000"
            placeholder="80000"
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="durataAnni" className="input-label">
            Durata (anni)
          </label>
          <select
            id="durataAnni"
            name="durataAnni"
            value={formData.durataAnni}
            onChange={handleChange}
            className="w-full"
          >
            {[5, 10, 15, 20, 25, 30].map((anni) => (
              <option key={anni} value={anni}>
                {anni} anni
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tipo Tasso Selector */}
      <div className="input-group">
        <label className="input-label">Tipo Tasso</label>
        <div className="grid grid-cols-3 gap-2">
          {(['fisso', 'variabile', 'misto'] as const).map((tipo) => (
            <button
              key={tipo}
              type="button"
              onClick={() => setTipoTasso(tipo)}
              className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                tipoTasso === tipo
                  ? 'bg-primary-100 border-primary-400 text-primary-800'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Rate Input — Fisso */}
      {tipoTasso === 'fisso' && (
        <div className="input-group">
          <label htmlFor="tassoInteresse" className="input-label">
            Tasso di Interesse Annuo (TAN) %
          </label>
          <div className="relative">
            <input
              type="number"
              id="tassoInteresse"
              name="tassoInteresse"
              value={formData.tassoInteresse}
              onChange={handleChange}
              className="w-full pr-8"
              min="0"
              max="30"
              step="0.1"
              placeholder="3.5"
              required
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
              %
            </span>
          </div>
        </div>
      )}

      {/* Rate Input — Variabile */}
      {tipoTasso === 'variabile' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div className="input-group">
              <label htmlFor="euribor" className="input-label">
                Euribor 3M (%)
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="euribor"
                  value={euribor}
                  onChange={(e) => setEuribor(e.target.value)}
                  className="w-full pr-8"
                  min="0"
                  max="15"
                  step="0.1"
                  placeholder="3.2"
                  required
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                  %
                </span>
              </div>
            </div>
            <div className="input-group">
              <label htmlFor="spread" className="input-label">
                Spread (%)
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="spread"
                  value={spread}
                  onChange={(e) => setSpread(e.target.value)}
                  className="w-full pr-8"
                  min="0"
                  max="10"
                  step="0.1"
                  placeholder="1.3"
                  required
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                  %
                </span>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            TAN calcolato: <span className="font-semibold">{tassoEffettivo.toFixed(2)}%</span>
          </p>
        </div>
      )}

      {/* Rate Input — Misto */}
      {tipoTasso === 'misto' && (
        <div className="space-y-4">
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 space-y-3">
            <p className="text-xs font-semibold text-primary-700 uppercase tracking-wide">
              Periodo Fisso
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="input-group">
                <label htmlFor="tanFisso" className="input-label">
                  TAN Fisso (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    id="tanFisso"
                    value={tanFisso}
                    onChange={(e) => setTanFisso(e.target.value)}
                    className="w-full pr-8"
                    min="0"
                    max="30"
                    step="0.1"
                    placeholder="3.5"
                    required
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    %
                  </span>
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="durataFisso" className="input-label">
                  Durata Fisso (anni)
                </label>
                <select
                  id="durataFisso"
                  value={durataFisso}
                  onChange={(e) => setDurataFisso(parseInt(e.target.value, 10))}
                  className="w-full"
                >
                  {[2, 3, 5, 7, 10, 15].map((anni) => (
                    <option key={anni} value={anni}>
                      {anni} anni
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-3">
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
              Periodo Variabile
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="input-group">
                <label htmlFor="euriborMisto" className="input-label">
                  Euribor 3M (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    id="euriborMisto"
                    value={euriborMisto}
                    onChange={(e) => setEuriborMisto(e.target.value)}
                    className="w-full pr-8"
                    min="0"
                    max="15"
                    step="0.1"
                    placeholder="3.2"
                    required
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    %
                  </span>
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="spreadMisto" className="input-label">
                  Spread (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    id="spreadMisto"
                    value={spreadMisto}
                    onChange={(e) => setSpreadMisto(e.target.value)}
                    className="w-full pr-8"
                    min="0"
                    max="10"
                    step="0.1"
                    placeholder="1.3"
                    required
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    %
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calculation Preview — Fisso / default */}
      {tipoTasso === 'fisso' && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-primary-800">Riepilogo Calcoli</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-primary-600">Rata Mensile</p>
              <p className="text-lg font-bold text-primary-800 currency">
                {formatCurrency(calculations.rataMensile)}
              </p>
            </div>
            <div>
              <p className="text-xs text-primary-600">Totale Interessi</p>
              <p className="text-lg font-bold text-warning-700 currency">
                {formatCurrency(calculations.totaleInteressi)}
              </p>
            </div>
            <div>
              <p className="text-xs text-primary-600">Totale da Pagare</p>
              <p className="text-lg font-bold text-gray-900 currency">
                {formatCurrency(calculations.totalePagato)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scenario Preview — Variabile */}
      {tipoTasso === 'variabile' && scenari && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-primary-800">Scenari Rata Mensile</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-green-600 font-medium">Ottimistico</p>
              <p className="text-xs text-gray-500">Euribor -1%</p>
              <p className="text-lg font-bold text-green-700 currency">
                {formatCurrency(scenari.ottimistico.rata)}
              </p>
              <p className="text-xs text-gray-500">
                TAN {scenari.ottimistico.tasso.toFixed(2)}%
              </p>
            </div>
            <div className="border-x border-primary-200 px-2">
              <p className="text-xs text-primary-600 font-medium">Base</p>
              <p className="text-xs text-gray-500">Attuale</p>
              <p className="text-lg font-bold text-primary-800 currency">
                {formatCurrency(scenari.base.rata)}
              </p>
              <p className="text-xs text-gray-500">
                TAN {scenari.base.tasso.toFixed(2)}%
              </p>
            </div>
            <div>
              <p className="text-xs text-red-600 font-medium">Pessimistico</p>
              <p className="text-xs text-gray-500">Euribor +2%</p>
              <p className="text-lg font-bold text-red-700 currency">
                {formatCurrency(scenari.pessimistico.rata)}
              </p>
              <p className="text-xs text-gray-500">
                TAN {scenari.pessimistico.tasso.toFixed(2)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scenario Preview — Misto */}
      {tipoTasso === 'misto' && scenarioMisto && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-primary-800">Riepilogo Misto</h4>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="bg-white rounded-lg p-3">
              <p className="text-xs text-primary-600 font-medium">
                Periodo Fisso ({scenarioMisto.durataFissoAnni} anni)
              </p>
              <p className="text-lg font-bold text-primary-800 currency">
                {formatCurrency(scenarioMisto.rataFisso)}
              </p>
              <p className="text-xs text-gray-500">
                TAN {tanFisso}%
              </p>
            </div>
            <div className="bg-white rounded-lg p-3">
              <p className="text-xs text-amber-600 font-medium">
                Periodo Variabile ({scenarioMisto.durataVariabileAnni} anni)
              </p>
              <p className="text-lg font-bold text-amber-700 currency">
                {formatCurrency(scenarioMisto.rataVariabile)}
              </p>
              <p className="text-xs text-gray-500">
                TAN stimato {scenarioMisto.tanVariabile.toFixed(2)}%
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="input-group">
        <label htmlFor="note" className="input-label">
          Note (opzionale)
        </label>
        <textarea
          id="note"
          name="note"
          value={formData.note}
          onChange={handleChange}
          className="w-full"
          rows={2}
          placeholder="es. Tasso fisso, spread 1.5%"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary flex-1"
          disabled={isLoading}
        >
          Annulla
        </button>
        <button
          type="submit"
          className="btn-primary flex-1"
          disabled={isLoading}
        >
          {isLoading ? 'Salvataggio...' : finanziamento ? 'Salva Modifiche' : 'Aggiungi Mutuo'}
        </button>
      </div>
    </form>
  );
}
